import React, { useEffect, useState } from 'react';
import { Avatar as MuiAvatar, Box, Badge } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useUserStatus } from '../hooks/useUserStatus';
import frameSet1 from '../assets/frames/frame-set-1.png';
import SupporterBadge from './SupporterBadge';

// Styled Badge for online indicator
const StyledBadge = styled(Badge)(({ theme, status, avatarsize }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'online':
        return '#4CAF50'; // Green
      case 'away':
        return '#FFC107'; // Yellow
      case 'busy':
        return '#F44336'; // Red
      case 'offline':
      default:
        return '#9E9E9E'; // Gray
    }
  };

  // Calculate badge size proportional to avatar (15% of avatar size, min 10px, max 14px)
  const badgeSize = Math.min(Math.max(avatarsize * 0.15, 10), 14);
  const borderWidth = 2;
  // Fixed offset from edge - stays consistent regardless of avatar size
  const badgeOffset = Math.max(2, avatarsize * 0.08);

  return {
    '& .MuiBadge-badge': {
      backgroundColor: getStatusColor(),
      color: getStatusColor(),
      width: badgeSize,
      height: badgeSize,
      minWidth: badgeSize,
      minHeight: badgeSize,
      borderRadius: '50%',
      border: `${borderWidth}px solid ${theme.palette.background.paper}`,
      boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
      // Fixed positioning at bottom-right - consistent across all avatar sizes
      right: badgeOffset,
      bottom: badgeOffset,
      transform: 'translate(0, 0)',
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: status === 'online' ? 'ripple 1.2s infinite ease-in-out' : 'none',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
  };
});

const Avatar = ({
  src,
  alt,
  size = 40,
  name,
  showIndicator = false,
  isOnline = false,
  showBorderIndicator = false,
  borderColor,
  onClick,
  sx,
  userId = null,
  user = null,
  showFrame = false, // Show avatar frame decoration
  frameVisible = true, // Control frame visibility
  frameIndex = 0, // Frame index (0-5) from the sprite sheet
  frameUrl = null, // DB-managed premium frame asset URL (overrides sprite when set)
  variant = 'circular', // 'circular' | 'rounded' | 'square' - frames only show on circular
  fullWidth = false,
  disableMarginRight = false,
  containerSx,
  isPremium = false, // Show SupporterBadge in bottom-right corner
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);
  const cacheBustRef = React.useRef(Date.now());
  
  // Reset error state when src changes and update cache bust timestamp
  useEffect(() => {
    setImageError(false);
    setImageSrc(src);
    // Update cache bust timestamp when src changes to force refresh
    cacheBustRef.current = Date.now();
  }, [src]);
  
  // Get real-time online status if userId is provided
  const { isOnline: realtimeIsOnline, status } = useUserStatus(userId);
  
  // Use real-time status if userId provided, otherwise use prop
  const userStatus = userId ? status : (user?.onlineStatus || (isOnline ? 'online' : 'offline'));
  // Derive isOnline from status - if status is not 'offline', user is online
  const userIsOnline = userId ? (status !== 'offline') : (user?.onlineStatus === 'online' || isOnline);
  
  const handleImageError = () => {
    setImageError(true);
    setImageSrc(null);
  };
  
  // Add cache-busting parameter to force refresh when src changes
  const getImageSrcWithCacheBust = () => {
    if (!imageSrc || imageError) return null;
    // Add cache-busting timestamp - append to existing query params or add new one
    if (imageSrc.includes('?')) {
      return `${imageSrc}&t=${cacheBustRef.current}`;
    }
    return `${imageSrc}?t=${cacheBustRef.current}`;
  };

  const getStatusColor = () => {
    switch (userStatus) {
      case 'online':
        return '#4CAF50'; // Green
      case 'away':
        return '#FFC107'; // Yellow
      case 'busy':
        return '#F44336'; // Red
      case 'offline':
      default:
        return '#9E9E9E'; // Gray
    }
  };
  
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const isCircular = variant === 'circular';
  const isSquare = variant === 'square' || variant === 'rounded';

  // Calculate border width for proper frame sizing
  const borderWidth = showIndicator && userIsOnline 
    ? 3 
    : showBorderIndicator 
      ? 2 
      : 0;
  
  // Define border colors/gradients for square avatars based on frameIndex
  const getSquareBorderStyle = (index) => {
    const borderStyles = [
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #6B8EAD, #A8C5E0) 1', boxShadow: '0 0 15px rgba(107, 142, 173, 0.5)' }, // Silver/Blue
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #9B7EBF, #D4B5E8) 1', boxShadow: '0 0 15px rgba(155, 126, 191, 0.5)' }, // Purple
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #4A9B9B, #7DD4D4) 1', boxShadow: '0 0 15px rgba(74, 155, 155, 0.5)' }, // Teal
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #FFD700, #FFA500) 1', boxShadow: '0 0 20px rgba(255, 215, 0, 0.6)' }, // Gold
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #CD7F32, #FF8C42) 1', boxShadow: '0 0 15px rgba(205, 127, 50, 0.5)' }, // Bronze
      { border: '4px solid', borderImage: 'linear-gradient(135deg, #6A4C93, #9B7EBF) 1', boxShadow: '0 0 15px rgba(106, 76, 147, 0.5)' }, // Dark Purple
    ];
    return borderStyles[index % 6];
  };
  
  // Frame should be larger than avatar to show decorative elements (crown, etc.)
  const frameScale = 1.35; // Frame is 35% larger than avatar
  const frameSize = size * frameScale;
  const frameOffset = (frameSize - size) / 2; // Center the frame over the avatar

  const avatarElement = (
    <MuiAvatar
      src={src}
      alt={alt || name}
      onClick={onClick}
      variant={variant}
      sx={{
        width:  fullWidth ? '100%' : size,
        height: fullWidth ? '100%' : size,
        fontSize: size * 0.4,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'primary.light',
        color: 'primary.main',
        fontWeight: 600,
        border: showFrame && frameVisible && isSquare
          ? getSquareBorderStyle(frameIndex).border
          : showIndicator && userIsOnline 
            ? `3px solid ${getStatusColor()}` 
            : showBorderIndicator 
              ? `2px solid ${borderColor || 'primary.main'}` 
              : 'none',
        borderImage: showFrame && frameVisible && isSquare
          ? getSquareBorderStyle(frameIndex).borderImage
          : 'none',
        boxShadow: showFrame && frameVisible && isSquare
          ? getSquareBorderStyle(frameIndex).boxShadow
          : 'none',
        ...sx,
      }}
    >
      {!src && getInitials(name || alt || '')}
    </MuiAvatar>
  );

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', width: size, height: size, mr: 2 }}>
      {avatarElement}
      {/* Avatar Frame — DB-managed asset (premium) */}
      {frameUrl && (
        <Box
          component="img"
          src={frameUrl}
          alt=""
          sx={{
            position: 'absolute',
            top: -frameOffset,
            left: -frameOffset,
            width: frameSize,
            height: frameSize,
            pointerEvents: 'none',
            zIndex: 2,
            objectFit: 'contain',
          }}
        />
      )}
      {/* SupporterBadge — shown when isPremium prop is true */}
      {isPremium && (
        <Box sx={{ position: 'absolute', bottom: -2, right: -2, zIndex: 3 }}>
          <SupporterBadge size={Math.max(14, size * 0.32)} />
        </Box>
      )}
      {/* Avatar Frame — sprite sheet (legacy / non-premium) */}
      {showFrame && frameVisible && isCircular && !frameUrl && (
        <Box
          sx={{
            position: 'absolute',
            top: -frameOffset - (129),
            left: -frameOffset - (31.90),
            width: frameSize * 1.40,
            height: frameSize * 1.50,
            backgroundImage: `url(${frameSet1})`,
            backgroundSize: `${6 * 100}% 100%`, // 6 frames in sprite
            backgroundPosition: `${(frameIndex % 6) * (100 / 5)}% 0`, // Calculate position for selected frame
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
};

export default Avatar;
